import { useMemo, FormEvent } from 'react'
import { router, useForm } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { AlertCircle, Clock, Zap } from 'lucide-react'

interface AdminPoolRolloutsNewProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

function New(_props: AdminPoolRolloutsNewProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: '',
    target_count: 1000,
    instances_per_minute: 3,
    source_repo_name: 'starter-base',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/pool_rollouts')
  }

  // Calculate estimated completion time
  const estimatedHours = useMemo(() => {
    const instancesPerHour = data.instances_per_minute * 60
    if (instancesPerHour <= 0) return 0
    return (data.target_count / instancesPerHour).toFixed(1)
  }, [data.target_count, data.instances_per_minute])

  const estimatedCompletion = useMemo(() => {
    const instancesPerHour = data.instances_per_minute * 60
    if (instancesPerHour <= 0) return null
    const hours = data.target_count / instancesPerHour
    const completionDate = new Date(Date.now() + hours * 60 * 60 * 1000)
    return completionDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }, [data.target_count, data.instances_per_minute])

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Pool Rollouts', href: '/admin/pool_rollouts' },
          { label: 'New' },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-2xl">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">Create Pool Rollout</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Configure a new mass provisioning rollout for the dev instance pool
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <Card className="shadow-none p-6 space-y-6">
                  {/* Rollout Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      Rollout Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Jan 2026 - 4K Pool Expansion"
                      value={data.name}
                      onChange={(e) => setData('name', e.target.value)}
                      required
                    />
                    {errors.name && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      A descriptive name to identify this rollout
                    </p>
                  </div>

                  {/* Target Count */}
                  <div className="space-y-2">
                    <Label htmlFor="target_count">
                      Target Count <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="target_count"
                      type="number"
                      min={1}
                      max={10000}
                      value={data.target_count}
                      onChange={(e) => setData('target_count', parseInt(e.target.value) || 0)}
                      required
                    />
                    {errors.target_count && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.target_count}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Total number of instances to create (1-10,000)
                    </p>
                  </div>

                  {/* Instances Per Minute */}
                  <div className="space-y-2">
                    <Label htmlFor="instances_per_minute">
                      Instances Per Minute <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="instances_per_minute"
                      type="number"
                      min={1}
                      max={30}
                      value={data.instances_per_minute}
                      onChange={(e) => setData('instances_per_minute', parseInt(e.target.value) || 1)}
                      required
                    />
                    {errors.instances_per_minute && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.instances_per_minute}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Rate of instance creation (1-30 per minute). Higher rates require more Solid Queue workers.
                    </p>
                  </div>

                  {/* Source Repo */}
                  <div className="space-y-2">
                    <Label htmlFor="source_repo_name">
                      Source Repository
                    </Label>
                    <Input
                      id="source_repo_name"
                      type="text"
                      value={data.source_repo_name}
                      onChange={(e) => setData('source_repo_name', e.target.value)}
                      className="font-mono"
                    />
                    {errors.source_repo_name && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {errors.source_repo_name}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Template repository to use for instances (default: starter-base)
                    </p>
                  </div>
                </Card>

                {/* Estimated Completion Preview */}
                <Card className="shadow-none p-6 bg-blue-500/5 border-blue-500/20">
                  <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Estimated Completion
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Duration</p>
                      <p className="text-2xl font-bold text-foreground">
                        {estimatedHours} hours
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Completion Time</p>
                      <p className="text-2xl font-bold text-foreground">
                        {estimatedCompletion || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Rate</p>
                      <p className="text-lg font-semibold text-foreground">
                        {data.instances_per_minute * 60}/hour
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Workers Recommended</p>
                      <p className="text-lg font-semibold text-foreground">
                        {Math.max(24, data.instances_per_minute * 2)}+
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Rate Guide */}
                <Card className="shadow-none p-6 bg-muted/50">
                  <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Rate Guide
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="font-medium">Conservative</p>
                      <p className="text-muted-foreground">1-2/min</p>
                      <p className="text-xs text-muted-foreground">~83 hrs for 10K</p>
                    </div>
                    <div>
                      <p className="font-medium">Standard</p>
                      <p className="text-muted-foreground">3-5/min</p>
                      <p className="text-xs text-muted-foreground">~28 hrs for 10K</p>
                    </div>
                    <div>
                      <p className="font-medium">Aggressive</p>
                      <p className="text-muted-foreground">10-30/min</p>
                      <p className="text-xs text-muted-foreground">~6 hrs for 10K</p>
                    </div>
                  </div>
                </Card>

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.visit('/admin/pool_rollouts')}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={processing}>
                    {processing ? 'Creating...' : 'Create Rollout'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
